import { useGlobalSearchHistoryTracking } from '@/hooks/use-global-search-tracking';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  getContactDeails,
  getJobDetails,
  getFlowDetails,
  getInterviewDetails,
} from './api';
import { initialSearchResults } from './search-manager';
import {
  GetPermittedSearchRequestsProps,
  HandleSearchProps,
  SearchResults,
} from './types';

const getPermittedSearchRequests = ({
  permittedTabIds,
  searchTerm,
  signal,
  mutations,
  includeResume,
}: GetPermittedSearchRequestsProps) => {
  const requests: Record<string, Promise<any>> = {
    contacts: Promise.resolve(null),
    jobs: Promise.resolve(null),
    flows: Promise.resolve(null),
    interviews: Promise.resolve(null),
  };

  if (permittedTabIds.includes('contacts')) {
    requests.contacts = mutations.contacts.mutateAsync({
      searchTerm,
      includeResume,
      signal,
    });
  }
  if (permittedTabIds.includes('jobs')) {
    requests.jobs = mutations.jobs.mutateAsync({ searchTerm, signal });
  }
  if (permittedTabIds.includes('flows')) {
    requests.flows = mutations.flows.mutateAsync({ searchTerm, signal });
  }
  if (permittedTabIds.includes('interviews')) {
    requests.interviews = mutations.interviews.mutateAsync({
      searchTerm,
      signal,
    });
  }

  return Promise.all([
    requests.contacts,
    requests.jobs,
    requests.flows,
    requests.interviews,
  ]);
};

export const useSearchHelper = () => {
  const contactDetailsMutation = useMutation({ mutationFn: getContactDeails });
  const jobDetailsMutation = useMutation({ mutationFn: getJobDetails });
  const flowDetailsMutation = useMutation({ mutationFn: getFlowDetails });
  const interviewDetailsMutation = useMutation({
    mutationFn: getInterviewDetails,
  });
  const { trackSearchTerm } = useGlobalSearchHistoryTracking();
  const controllerRef = useRef<AbortController | null>(null);

  const handleSearch = async ({
    searchTerm,
    setIsSearchLoading,
    setSearchResults,
    permittedTabIds,
    includeResume,
  }: HandleSearchProps) => {
    if (searchTerm?.length >= 2) {
      // Cancel previous
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        setIsSearchLoading(true);

        const [contactApiData, jobApiData, flowApiData, interviewApiData] =
          await getPermittedSearchRequests({
            permittedTabIds,
            searchTerm,
            signal: controller.signal,
            includeResume,
            mutations: {
              contacts: contactDetailsMutation,
              jobs: jobDetailsMutation,
              flows: flowDetailsMutation,
              interviews: interviewDetailsMutation,
            },
          });

        const searchResults = {
          ...initialSearchResults,
          contacts: {
            data:
              contactApiData?.contact?.length > 0
                ? contactApiData?.contact
                : [],
            total: contactApiData?.pagination?.total || 0,
          },
          jobs: {
            data: jobApiData?.ats_jobs?.length > 0 ? jobApiData?.ats_jobs : [],
            total: jobApiData?.pagination?.total || 0,
          },
          flows: {
            data:
              flowApiData?.outreaches?.length > 0
                ? flowApiData?.outreaches
                : [],
            total: flowApiData?.outreaches?.length || 0,
          },
          interviews: {
            data:
              interviewApiData?.interviews?.length > 0
                ? interviewApiData?.interviews
                : [],
            total: interviewApiData?.pagination?.count || 0,
          },
        };
        trackSearchTerm(searchTerm);
        setSearchResults(searchResults);
        setIsSearchLoading(false);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        // Handle error later
      }
    } else {
      setIsSearchLoading(false);
      setSearchResults(initialSearchResults);
    }
  };

  const handleResumeSearch = async ({
    searchTerm,
    setIsSearchLoading,
    setSearchResults,
    includeResume,
    searchResults,
  }: HandleSearchProps & { searchResults: SearchResults }) => {
    if (searchTerm?.length >= 2) {
      // Cancel previous
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        setIsSearchLoading(true);
        const response = await contactDetailsMutation.mutateAsync({
          searchTerm,
          includeResume,
          signal: controller.signal,
        });

        const newSearchResults = {
          ...searchResults,
          contacts: {
            data: response?.contact?.length > 0 ? response?.contact : [],
            total: response?.pagination?.total || 0,
          },
        };

        setSearchResults(newSearchResults);
        setIsSearchLoading(false);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
      }
    }
  };
  return { handleSearch, handleResumeSearch };
};
